import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { RecordIconTile } from '../common/RecordBadge';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import ShellCrumbs from './ShellCrumbs';
import ShellTabs from './ShellTabs';
import useShellPin from './useShellPin';
import useTabsFit from './useTabsFit';
import useWindowClass from '../../hooks/useWindowClass';
import { Skeleton } from '../loading';

/**
 * THE SHELL'S HEADER (docs/platform-standards.md §13). It answers three
 * questions, where am I (crumbs), what is this (scope) and what can I do
 * (tabs), and is driven by a config (config/collectionShell.js), never
 * assembled by a page. On desktop it is two rows (KPR-818), the tabs inline
 * in front of the actions:
 *
 *   All collections › Parent › Biewend Armory
 *   [tile]  COLLECTION              Home  Items  Reports  Cards  Settings  ⋯  [Add item]
 *           Biewend Armory
 *
 * When the badge, the tabs and the actions do not fit one line (a narrow
 * window, a long title), and always in a compact window, the tabs take their own row
 * beneath the badge (useTabsFit); the title is never truncated to make room.
 * The bottom border wears the collection's color (`--collection-accent`, set
 * by the layout), and the neutral border while it loads or has none.
 *
 * Tabs are real links (ShellTabs), so middle-click and copy-link work and the
 * current one carries `aria-current="page"`. The crumb (ShellCrumbs) never
 * marks the current section: that is the tab's job. The browser tab is named
 * by the badge's title, as the old context bar named it.
 *
 * `actions` is the scope's own cluster (the collection's ⋯ and primary);
 * `actionsSlotRef` hands the layout the node a page's <ShellActions> portals
 * into, drawn before it.
 */
export default function ShellHeader({
	config,
	actions = null,
	actionsSlotRef,
	below = null,
	state = 'ready',
	style,
	className = '',
	...rest
}) {
	const { crumbs = [], scope = {}, tabs = [] } = config || {};
	// On desktop the header is pinned; it publishes its height for whatever has to
	// sit beneath it (useShellPin). Nothing is pinned on a phone.
	const headerRef = useRef(null);
	useShellPin(headerRef);
	// compact (§18): a 36px tile beside a 17px title (shell-header.css shrinks the title).
	const { compact } = useWindowClass();
	const tilePx = compact ? 36 : 48;
	const identityRef = useRef(null);
	const tabsInline = useTabsFit(headerRef, identityRef, {
		enabled: !compact && tabs.length > 0,
		deps: [scope.title, scope.kicker, scope.loading, tabs.length],
	});
	const tabTitle = scope.documentTitle ?? scope.title;
	useDocumentTitle(typeof tabTitle === 'string' && !scope.loading ? tabTitle : '');

	// Focus follows a navigation (KPR-641): when the PATH changes (not on first
	// draw, and not for a query-only change such as Settings' ?tab=) the page's
	// one heading takes focus, so a keyboard or screen reader person starts at the
	// top of what they opened. The heading is tabIndex -1: focusable, never a stop.
	const { pathname } = useLocation();
	const lastPath = useRef(pathname);
	useEffect(() => {
		if (lastPath.current === pathname) return;
		lastPath.current = pathname;
		headerRef.current?.querySelector('[data-shell-title]')?.focus({ preventScroll: true });
	}, [pathname]);

	const classes = ['shell-header', tabsInline ? 'shell-header--tabs-inline' : 'shell-header--tabs-below', className].filter(Boolean).join(' ');

	return (
		<header ref={headerRef} className={classes} style={style} data-shell-header data-shell-scope="collection" data-shell-state={scope.loading ? 'loading' : state} data-shell-tabs-placement={tabsInline ? 'inline' : 'below'} {...rest}>
			<ShellCrumbs crumbs={crumbs} />

			{/* One grid for the badge, the tabs and the actions, in reading (and
			   focus) order: title, tabs, actions. shell-header.css places them on
			   one line or puts the tabs beneath. */}
			<div className="shell-header__identity" ref={identityRef}>
				<div className="shell-header__badge" data-shell-badge>
					{scope.loading ? (
						<Skeleton variant="avatar" size={tilePx} className="shell-header__tile shell-header__tile--pending" />
					) : scope.record !== undefined || scope.kind ? (
						<RecordIconTile record={scope.record} kind={scope.kind || 'collection'} size={tilePx} className="shell-header__tile" />
					) : null}
					<div className="shell-header__titles">
						{scope.eyebrow ? <span className="shell-header__eyebrow" data-shell-eyebrow>{scope.eyebrow}</span> : null}
						{scope.loading ? (
							<h1 className="shell-header__title shell-header__title--pending" aria-label="Loading collection" aria-busy="true" tabIndex={-1} data-shell-title>
								<Skeleton variant="line" width={220} />
							</h1>
						) : (
							<h1 className="shell-header__title" tabIndex={-1} data-shell-title>{scope.title}</h1>
						)}
						{scope.kicker || below ? (
							<div className="shell-header__below">
								{scope.kicker ? <span className="shell-header__kicker">{scope.kicker}</span> : null}
								{below}
							</div>
						) : null}
					</div>
				</div>
				<ShellTabs tabs={tabs} />
				<div className="shell-header__actions">
					<div className="shell-header__slot" ref={actionsSlotRef} data-shell-actions-slot />
					{actions}
				</div>
			</div>
		</header>
	);
}
